import axios from 'axios';

const LEGACY_API_HOST = 'bacchapanti.perfeasy.com';
const PRIMARY_APP_HOST = 'crm.bacchapanti.com';

function isBacchapantiAppHost(hostname) {
  return hostname === PRIMARY_APP_HOST || hostname.endsWith('.bacchapanti.com');
}

// API base URL: prefer same-origin on crm.bacchapanti.com (nginx proxies /api), else env, else dev defaults.
export function getApiBaseURL() {
  if (typeof window !== 'undefined') {
    const { protocol, hostname, port } = window.location;

    // New domain serves UI + API on the same host — ignore stale REACT_APP_API_URL pointing at perfeasy.com.
    if (isBacchapantiAppHost(hostname)) {
      return '';
    }

    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;

    if (port === '4001') return `${protocol}//${hostname}:4000`; // CRA dev: frontend :4001 -> API :4000
    if (hostname === LEGACY_API_HOST || hostname.endsWith('.perfeasy.com')) {
      return ''; // legacy domain also proxies /api on same host
    }
    return ''; // same origin (e.g. nginx proxies /api to backend)
  }
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return 'http://localhost:4000';
}

/**
 * Backend often returns public paths like `/uploads/...`. Those must be opened on the API origin:
 * with CRA on :4001 and API on :4000, `window.open('/uploads/...')` hits the wrong server and fails.
 */
export function resolvePublicAssetUrl(pathOrUrl) {
  const s = String(pathOrUrl || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('//')) {
    if (typeof window !== 'undefined') return `${window.location.protocol}${s}`;
    return `https:${s}`;
  }
  const path = s.startsWith('/') ? s : `/${s}`;
  const base = getApiBaseURL();
  if (!base) return path;
  return `${base.replace(/\/$/, '')}${path}`;
}

function downloadBlobFile(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function homeworkPdfFilename(pdfUrl, fallback = 'homework.pdf') {
  const fromPath = String(pdfUrl || '')
    .split('/')
    .filter(Boolean)
    .pop();
  if (fromPath && /\.pdf$/i.test(fromPath)) return fromPath;
  return fallback;
}

async function parseHomeworkPdfBlobResponse(res) {
  const blob = res.data;
  if (blob instanceof Blob && blob.type && blob.type.includes('application/json')) {
    const text = await blob.text();
    let msg = 'Could not load PDF';
    try {
      const j = JSON.parse(text);
      msg = j.message || msg;
    } catch (_) {
      /* ignore */
    }
    throw new Error(msg);
  }
  return blob instanceof Blob ? blob : new Blob([blob], { type: 'application/pdf' });
}

async function fetchSessionHomeworkPdfBlob(sessionId, pdfUrl) {
  const sid = String(sessionId || '').trim();
  const pu = String(pdfUrl || '').trim();
  if (!sid || !pu) throw new Error('Missing session or PDF path');
  try {
    const res = await api.get(`/api/teacher/sessions/${encodeURIComponent(sid)}/homework-asset`, {
      params: { url: pu },
      responseType: 'blob',
      timeout: 120000,
    });
    const out = await parseHomeworkPdfBlobResponse(res);
    return { blob: out, filename: homeworkPdfFilename(pu) };
  } catch (e) {
    const errBlob = e.response?.data;
    if (errBlob instanceof Blob && e.response?.status >= 400) {
      try {
        const text = await errBlob.text();
        const j = JSON.parse(text);
        throw new Error(j.message || 'Could not load PDF');
      } catch (inner) {
        if (inner instanceof SyntaxError) {
          throw new Error('Could not load PDF');
        }
        throw inner;
      }
    }
    throw new Error(e.response?.data?.message || e.message || 'Could not load PDF');
  }
}

/**
 * Save homework PDF to disk (reliable). Prefer this over browser download from blob: viewer tab.
 */
export async function downloadSessionHomeworkPdf(sessionId, pdfUrl, filename) {
  const { blob, filename: derived } = await fetchSessionHomeworkPdfBlob(sessionId, pdfUrl);
  const name = String(filename || derived || 'homework.pdf').trim() || 'homework.pdf';
  downloadBlobFile(blob, name);
}

/**
 * Preview homework PDF in a new tab. Browser "Save" on that tab may fail for blob: URLs;
 * use downloadSessionHomeworkPdf for saving.
 */
export async function openSessionHomeworkPdf(sessionId, pdfUrl) {
  const { blob } = await fetchSessionHomeworkPdfBlob(sessionId, pdfUrl);
  const blobUrl = URL.createObjectURL(blob);
  const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
  if (!w) {
    URL.revokeObjectURL(blobUrl);
    throw new Error('Popup blocked — allow popups for this site');
  }
  // Keep blob alive long enough for in-tab viewing; do not revoke quickly (breaks Chrome save-from-viewer).
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60 * 60 * 1000);
}

const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper to get a UUID (supported in modern browsers)
const generateRequestId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    // Fallback simple random id
    return 'rid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  } catch {
    return 'rid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Attach correlation id and current route for server-side logs
    const reqId = generateRequestId();
    config.headers['x-request-id'] = reqId;
    config.headers['x-client-path'] = window.location?.pathname || '/';
    // Keep for error handler correlation
    config.metadata = { requestId: reqId, startTs: Date.now() };
    
    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('API Request:', config.method?.toUpperCase(), config.url, reqId);
    }
    
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ API Response:', response.status, response.config.url, response.config?.metadata?.requestId);
    }
    
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      url: error.config?.url,
      method: error.config?.method,
      requestId: error.config?.metadata?.requestId,
      message: error.response?.data?.message || error.message,
      errors: error.response?.data?.errors
    });
    
    // Handle expired-session 401s. Login itself also returns 401 for wrong
    // credentials — do not redirect, so the form can show that message.
    const status = error.response?.status;
    const requestUrl = String(error.config?.url || '');
    const isLoginAttempt = requestUrl.includes('/api/auth/login');
    // WhatsApp returns UNAUTHENTICATED when its own key is rejected. That is
    // not an expired CRM session, and must not clear the agent login.
    const providerUnauthenticated = error.response?.data?.code === 'UNAUTHENTICATED';
    if (status === 401 && !isLoginAttempt && !providerUnauthenticated) {
      console.log('🔐 API Auth Failure - 401 Unauthorized detected:', {
        url: error.config?.url,
        method: error.config?.method,
        requestId: error.config?.metadata?.requestId,
        message: error.response?.data?.message,
        currentPath: window.location?.pathname,
        hasToken: !!localStorage.getItem('token')
      });

      try {
        const cfg = error.config || {};
        const failure = {
          ts: new Date().toISOString(),
          requestId: cfg.metadata?.requestId,
          method: cfg.method?.toUpperCase?.() || 'GET',
          url: cfg.url,
          clientPath: cfg.headers?.['x-client-path'],
          status: status,
          responseMessage: error.response?.data?.message || null,
          responseErrors: error.response?.data?.errors || null,
        };
        // Persist so it survives the redirect
        sessionStorage.setItem('lastAuthFailure', JSON.stringify(failure));
        console.log('📝 API Auth Failure - Stored failure details:', failure);
      } catch (e) {
        console.error('❌ API Auth Failure - Failed to store failure details:', e);
      }

      // Clear token and redirect to login
      console.log('🚪 API Auth Failure - Clearing token and redirecting to login');
      localStorage.removeItem('token');
      if (window.location?.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/api/auth/login', credentials),
  register: (userData) => api.post('/api/auth/register', userData),
  registerStudent: (studentData) => api.post('/api/auth/register/student', studentData),
  logout: () => api.post('/api/auth/logout'),
  getProfile: () => api.get('/api/auth/me'),
  updateProfile: (profileData) => api.patch('/api/auth/me', profileData),
  changePassword: (passwords) => api.post('/api/auth/change-password', passwords),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }, { timeout: 30000 }),
  resetPassword: (token, newPassword) => api.post('/api/auth/reset-password', { token, newPassword }),
};

export const studentsAPI = {
  getAll: (params) => api.get('/api/students', { params }),
  getById: (id) => api.get(`/api/students/${id}`),
  create: (studentData) => api.post('/api/students', studentData),
  update: (id, studentData) => api.put(`/api/students/${id}`, studentData),
  delete: (id) => api.delete(`/api/students/${id}`),
  getTimePreferences: (id) => api.get(`/api/students/${id}/time-preferences`),
  updateTimePreferences: (id, preferences) => api.put(`/api/students/${id}/time-preferences`, preferences),
  getEnrollmentStatus: (id) => api.get(`/api/students/${id}/enrollment-status`),
  notifyEnrollmentSupervisor: (id) => api.post(`/api/students/${id}/enrollment/notify-supervisor`),
  notifyEnrollmentParent: (id) => api.post(`/api/students/${id}/enrollment/notify-parent`),
};

export const batchesAPI = {
  getAll: (params) => api.get('/api/batches', { params }),
  getById: (id, params) => api.get(`/api/batches/${id}`, { params }),
  create: (batchData) => api.post('/api/batches', batchData),
  update: (id, batchData) => api.put(`/api/batches/${id}`, batchData),
  delete: (id) => api.delete(`/api/batches/${id}`),
  getSchedules: (id) => api.get(`/api/batches/${id}/schedules`),
  updateSchedules: (id, schedules) => api.put(`/api/batches/${id}/schedules`, schedules),
  getStudents: (id) => api.get(`/api/batches/${id}/students`),
  getTeachersWithCounts: (params) => api.get('/api/batches/teachers-with-counts', { params }),
};

export const teachersAPI = {
  getAll: (params) => api.get('/api/teachers', { params }),
  getById: (id) => api.get(`/api/teachers/${id}`),
  create: (teacherData) => api.post('/api/teachers', teacherData),
  update: (id, teacherData) => api.put(`/api/teachers/${id}`, teacherData),
  delete: (id) => api.delete(`/api/teachers/${id}`),
  getAvailability: (id) => api.get(`/api/teachers/${id}/availability`),
  updateAvailability: (id, availability) => api.put(`/api/teachers/${id}/availability`, availability),
};

export const allocationsAPI = {
  suggestBatches: (data) => api.post('/api/allocations/suggest', data),
  assignStudent: (data) => api.post('/api/allocations/assign', data),
  validateAssignment: (params) => api.get('/api/allocations/validation', { params }),
  getStudentAssignments: (studentId) => api.get(`/api/allocations/student/${studentId}`),
  getBatchAssignments: (batchId) => api.get(`/api/allocations/batch/${batchId}`),
  removeAssignment: (assignmentId) => api.delete(`/api/allocations/${assignmentId}`),
  unassignOnLeave: (assignmentId, data) => api.post(`/api/allocations/${assignmentId}/unassign`, data),
  updateAssignmentStatus: (assignmentId, status) => api.put(`/api/allocations/${assignmentId}/status`, { status }),
  getStats: (params) => api.get('/api/allocations/stats', { params }),
  bulkAssign: (data) => api.post('/api/allocations/bulk-assign', data),
  getUnassignedStudents: (params) => api.get('/api/allocations/unassigned-students', { params }),
  autoAssign: (data) => api.post('/api/allocations/auto-assign', data),
  getAvailableTeachers: (studentId, batchType) => api.get(`/api/allocations/available-teachers?studentId=${studentId}&batchType=${batchType}`),
};

export const reportsAPI = {
  getDashboardStats: (params) => api.get('/api/reports/dashboard', { params }),
  getAllocationStats: (params) => api.get('/api/reports/allocation-stats', { params }),
  getBatchPerformance: (params) => api.get('/api/reports/batch-performance', { params }),
  getStudentPerformance: (params) => api.get('/api/reports/student-performance', { params }),
  exportReport: (params) => api.get('/api/reports/export', { params, responseType: 'blob' }),
};

export const leadsAPI = {
  getAll: (params) => api.get('/api/leads', { params }),
  getById: (id) => api.get(`/api/leads/${id}`),
  create: (leadData) => api.post('/api/leads', leadData),
  update: (id, leadData) => api.put(`/api/leads/${id}`, leadData),
  updateStatus: (id, statusData) => api.patch(`/api/leads/${id}/status`, statusData),
  updateCreationDate: (id, dateData) => api.patch(`/api/leads/${id}/creation-date`, dateData), // Separate endpoint for creation date update
  checkEmail: (id) => api.get(`/api/leads/${id}/check-email`),
  convertToStudent: (id, data) => api.post(`/api/leads/${id}/convert`, data),
  generateEnrollmentLink: (id, data = {}) => api.post(`/api/leads/${id}/send-enrollment`, data),
  /** @deprecated Use generateEnrollmentLink */
  sendEnrollmentForm: (id, data = {}) => api.post(`/api/leads/${id}/send-enrollment`, data),
  delete: (id) => api.delete(`/api/leads/${id}`),
  getStatusSubStatusMapping: () => api.get('/api/leads/status-sub-status-mapping'),
  getAnalytics: (params) => api.get('/api/leads/analytics', { params }),
  /** Export leads as CSV (same query params as getAll, without page/limit). Returns blob. */
  exportCSV: (params) => api.get('/api/leads/export', { params, responseType: 'blob' }),
  // Lead activity timeline
  getTimeline: (id) => api.get(`/api/leads/${id}/timeline`),
  // Lead notes with soft delete
  getNotes: (id, params) => api.get(`/api/leads/${id}/notes`, { params }),
  addNote: (id, data) => api.post(`/api/leads/${id}/notes`, data),
  deleteNote: (id, noteId) => api.delete(`/api/leads/${id}/notes/${noteId}`),
  familyCheck: (params) => api.get('/api/leads/family-check', { params }),
};

export const demosAPI = {
  getAll: (params) => api.get('/api/demos', { params }),
  getById: (id) => api.get(`/api/demos/${id}`),
  create: (demoData) => api.post('/api/demos', demoData),
  update: (id, demoData) => api.put(`/api/demos/${id}`, demoData),
  updateStatus: (id, statusData) => api.patch(`/api/demos/${id}/status`, statusData),
  reschedule: (id, rescheduleData) => api.post(`/api/demos/${id}/reschedule`, rescheduleData),
  delete: (id) => api.delete(`/api/demos/${id}`),
  getAvailableSlots: (teacherId, date, studentTimezone) => api.get(`/api/demos/available-slots/${teacherId}`, { params: { date, studentTimezone } }),
};

// Sales APIs
export const salesAPI = {
  getAll: (params) => api.get('/api/sales', { params }),
  getById: (id) => api.get(`/api/sales/${id}`),
  create: (data) => api.post('/api/sales', data),
  update: (id, data) => api.put(`/api/sales/${id}`, data),
  delete: (id, params) => api.delete(`/api/sales/${id}`, { params }), // reassign_to_user_id optional
};

// Scheduling APIs (batch/teacher grids, student selector)
export const schedulingAPI = {
  getUnassignedStudents: (params) => api.get('/api/scheduling/unassigned-students', { params }),
  getStudentProfile: (studentId) => api.get('/api/scheduling/student-profile', { params: { studentId } }),
  getBatchGrid: (params) =>
    api.get('/api/scheduling/batch-grid', {
      params: {
        studentId: params.studentId,
        timeWindows: params.timeWindows,
        days: params.days || '',
        teacherIds: params.teacherIds || '',
        timeOfDayStart: params.timeOfDayStart || '',
        timeOfDayEnd: params.timeOfDayEnd || '',
      },
    }),
  getTeacherGrid: (params) =>
    api.get('/api/scheduling/teacher-grid', {
      params: {
        studentId: params.studentId,
        timeWindows: params.timeWindows,
        days: params.days || '',
        teacherIds: params.teacherIds || '',
        timeOfDayStart: params.timeOfDayStart || '',
        timeOfDayEnd: params.timeOfDayEnd || '',
      },
    }),
  getSuggestions: (params) => {
    const p = { studentId: params.studentId, daysPerWeek: params.daysPerWeek ?? 2 };
    if (params.limitExisting != null) p.limitExisting = params.limitExisting;
    if (params.limitNew != null) p.limitNew = params.limitNew;
    return api.get('/api/scheduling/suggestions', { params: p });
  },
};

async function messageFromBlobError(data, fallback) {
  if (!(data instanceof Blob)) return fallback;
  try {
    const parsed = JSON.parse(await data.text());
    return parsed.message || fallback;
  } catch {
    return fallback;
  }
}

/** Authenticated LMS file bytes. Do not open /uploads/... in a new tab — the SPA sends that path to the dashboard. */
export async function fetchLmsResourceFileBlob(resourceId) {
  try {
    const res = await api.get(`/api/lms/resources/${encodeURIComponent(resourceId)}/file`, {
      responseType: 'blob',
    });
    const blob = res.data;
    if (blob instanceof Blob && blob.type && blob.type.includes('application/json')) {
      throw new Error(await messageFromBlobError(blob, 'Could not open file'));
    }
    const headerType = String(res.headers?.['content-type'] || '').split(';')[0].trim();
    if (headerType && blob instanceof Blob && headerType !== blob.type && !headerType.includes('json')) {
      return new Blob([blob], { type: headerType });
    }
    return blob;
  } catch (err) {
    if (err instanceof Error && err.message && !err.response) throw err;
    const fallback = err.response?.data?.message || err.message || 'Could not open file';
    throw new Error(await messageFromBlobError(err.response?.data, fallback));
  }
}

// LMS APIs
export const lmsAPI = {
  // Content tree (full hierarchy in one request)
  getContentTree: () => api.get('/api/lms/content-tree'),

  // Programs
  getPrograms: () => api.get('/api/lms/programs'),
  createProgram: (data) => api.post('/api/lms/programs', data),
  updateProgram: (id, data) => api.put(`/api/lms/programs/${id}`, data),
  deleteProgram: (id) => api.delete(`/api/lms/programs/${id}`),

  // Courses
  getCourses: (params) => api.get('/api/lms/courses', { params }),
  createCourse: (data) => api.post('/api/lms/courses', data),
  updateCourse: (id, data) => api.put(`/api/lms/courses/${id}`, data),
  deleteCourse: (id) => api.delete(`/api/lms/courses/${id}`),

  // Modules
  getModules: (params) => api.get('/api/lms/modules', { params }),
  createModule: (data) => api.post('/api/lms/modules', data),
  updateModule: (id, data) => api.put(`/api/lms/modules/${id}`, data),
  deleteModule: (id) => api.delete(`/api/lms/modules/${id}`),

  // Months
  getMonths: (params) => api.get('/api/lms/months', { params }),
  createMonth: (data) => api.post('/api/lms/months', data),
  updateMonth: (id, data) => api.put(`/api/lms/months/${id}`, data),
  deleteMonth: (id) => api.delete(`/api/lms/months/${id}`),

  // Videos (legacy — still supported)
  getVideos: (params) => api.get('/api/lms/videos', { params }),
  createVideo: (data) => api.post('/api/lms/videos', data),
  updateVideo: (id, data) => api.put(`/api/lms/videos/${id}`, data),
  deleteVideo: (id) => api.delete(`/api/lms/videos/${id}`),

  // Learning Resources
  getResources: (params) => api.get('/api/lms/resources', { params }),
  createResource: (data) => {
    if (data instanceof FormData) {
      return api.post('/api/lms/resources', data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.post('/api/lms/resources', data);
  },
  updateResource: (id, data) => {
    if (data instanceof FormData) {
      return api.put(`/api/lms/resources/${id}`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    }
    return api.put(`/api/lms/resources/${id}`, data);
  },
  deleteResource: (id) => api.delete(`/api/lms/resources/${id}`),

  // Assignments
  getAssignments: () => api.get('/api/lms/assignments'),
  createAssignment: (data) => api.post('/api/lms/assignments', data),
  deleteAssignment: (id) => api.delete(`/api/lms/assignments/${id}`),

  // My content (teacher)
  getMyContent: (params) => api.get('/api/lms/my-content', { params }),

  // Progress
  updateProgress: (data) => api.post('/api/lms/progress', data),
  getProgress: (teacherId) => api.get(`/api/lms/progress/${teacherId}`),
  getMyProgress: () => api.get('/api/lms/my-progress'),

  // Evaluations
  getEvaluations: (params) => api.get('/api/lms/evaluations', { params }),
  submitEvaluation: (data) => api.post('/api/lms/evaluations', data),
  overrideScore: (id, data) => api.patch(`/api/lms/evaluations/${id}/override-score`, data),
};

export const emailAPI = {
  send: (data) => api.post('/api/email/send', data),
};

// Attendance APIs
export const attendanceAPI = {
  getSessions: (params) => api.get('/api/attendance/sessions', { params }),
  getSessionsByTeacher: (params) => api.get('/api/attendance/sessions/by-teacher', { params }),
  getSessionById: (id) => api.get(`/api/attendance/sessions/${id}`),
  getRecordsBySession: (sessionId) => api.get(`/api/attendance/sessions/${sessionId}/records`),
  generateSessions: (data) => api.post('/api/attendance/sessions/generate', data),
  upsertRecord: (data) => api.put('/api/attendance/records', data),
};

export const meetingsAPI = {
  list: (params) => api.get('/api/meetings', { params }),
  mine: (params) => api.get('/api/meetings/mine', { params }),
  get: (id) => api.get(`/api/meetings/${id}`),
  create: (data) => api.post('/api/meetings', data),
  update: (id, data) => api.patch(`/api/meetings/${id}`, data),
  setAttendance: (id, data) => api.patch(`/api/meetings/${id}/attendance`, data),
  remove: (id) => api.delete(`/api/meetings/${id}`),
};

export const timesheetsAPI = {
  getMyWeek: (params) => api.get('/api/timesheets/me/week', { params }),
  refreshWeek: (id) => api.post(`/api/timesheets/me/week/${id}/refresh`),
  patchLine: (weekId, lineId, data) =>
    api.patch(`/api/timesheets/me/week/${weekId}/lines/${lineId}`, data),
  addLine: (weekId, data) => api.post(`/api/timesheets/me/week/${weekId}/lines`, data),
  deleteLine: (weekId, lineId) => api.delete(`/api/timesheets/me/week/${weekId}/lines/${lineId}`),
  submitWeek: (id) => api.post(`/api/timesheets/me/week/${id}/submit`),
  getInbox: (params) => api.get('/api/timesheets/inbox', { params }),
  getWeek: (id) => api.get(`/api/timesheets/weeks/${id}`),
  reviewWeek: (id, data) => api.patch(`/api/timesheets/weeks/${id}/review`, data),
  exportCsvUrl: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return `/api/timesheets/export.csv${q ? `?${q}` : ''}`;
  },
  getProjects: () => api.get('/api/timesheets/catalog/projects'),
  getActivityTypes: () => api.get('/api/timesheets/catalog/activity-types'),
  createProject: (data) => api.post('/api/timesheets/catalog/projects', data),
  createActivityType: (data) => api.post('/api/timesheets/catalog/activity-types', data),
  getCapacity: (userId) => api.get(`/api/timesheets/catalog/capacity/${userId}`),
  putCapacity: (userId, data) => api.put(`/api/timesheets/catalog/capacity/${userId}`, data),
};

export const payrollAPI = {
  listPeople: () => api.get('/api/payroll/people'),
  saveSalary: (userId, data) => api.put(`/api/payroll/people/${userId}/salary`, data),
  getMonth: (year, month) => api.get(`/api/payroll/months/${year}/${month}`),
  openMonth: (year, month) => api.post(`/api/payroll/months/${year}/${month}/open`),
  generateMonth: (year, month) => api.post(`/api/payroll/months/${year}/${month}/generate`),
  generateExit: (year, month, userId) => api.post(`/api/payroll/months/${year}/${month}/exit/${userId}`),
  processExit: (year, month, userId) => api.post(`/api/payroll/months/${year}/${month}/exit/${userId}/process`),
  markDay: (year, month, userId, data) => api.post(`/api/payroll/months/${year}/${month}/days/${userId}`, data),
  processMonth: (year, month) => api.post(`/api/payroll/months/${year}/${month}/process`),
  reopenMonth: (year, month) => api.post(`/api/payroll/months/${year}/${month}/reopen`),
  myPayslip: (year, month, kind) => api.get(`/api/payroll/me/${year}/${month}`, { params: kind ? { kind } : {} }),
  downloadCsv: (year, month) => api.get(`/api/payroll/months/${year}/${month}/export.csv`, { responseType: 'blob' }),
  getSetup: () => api.get('/api/payroll/setup'),
  saveTemplate: (data) => api.post('/api/payroll/setup/templates', data),
  deleteTemplate: (id) => api.delete(`/api/payroll/setup/templates/${id}`),
  saveEmployer: (data) => api.put('/api/payroll/setup/employer', data),
  confirmBasis: (data) => api.post('/api/payroll/setup/statute-basis', data),
  extendIncentive: (userId, incentiveId, data) => api.post(`/api/payroll/people/${userId}/incentives/${incentiveId}/extend`, data),
  saveIncentiveRate: (data) => api.post('/api/payroll/setup/incentive-rates', data),
  saveIncentiveCategory: (data) => api.post('/api/payroll/setup/incentive-categories', data),
  saveRule: (data) => api.post('/api/payroll/setup/rules', data),
  setReviewDate: (itemKey, data) => api.put(`/api/payroll/rule-reviews/${itemKey}`, data),
  getProfile: (userId) => api.get(`/api/payroll/people/${userId}`),
  saveProfile: (userId, data) => api.put(`/api/payroll/people/${userId}`, data),
  saveLocation: (userId, data) => api.post(`/api/payroll/people/${userId}/location`, data),
  acknowledgeLocation: (userId, data) => api.post(`/api/payroll/people/${userId}/location/acknowledge`, data),
  setApplicability: (userId, data) => api.post(`/api/payroll/people/${userId}/applicability`, data),
  saveIncentive: (userId, data) => api.post(`/api/payroll/people/${userId}/incentives`, data),
  addEducation: (userId, data) => api.post(`/api/payroll/people/${userId}/education`, data),
  addPreviousEmployment: (userId, data) => api.post(`/api/payroll/people/${userId}/previous-employment`, data),
  addDocument: (userId, data) => api.post(`/api/payroll/people/${userId}/documents`, data),
  markWeek: (year, month, weekId, data) => api.post(`/api/payroll/months/${year}/${month}/weeks/${weekId}`, data),
  setManualStatute: (year, month, userId, data) => api.post(`/api/payroll/months/${year}/${month}/statutes/${userId}`, data),
};

export const staffLeaveAPI = {
  getMine: () => api.get('/api/staff-leave/me'),
  apply: (data) => api.post('/api/staff-leave/me', data),
  cancel: (id) => api.post(`/api/staff-leave/me/${id}/cancel`),
  getInbox: () => api.get('/api/staff-leave/inbox'),
  decide: (id, data) => api.post(`/api/staff-leave/inbox/${id}/decision`, data),
  listTemplates: () => api.get('/api/staff-leave/setup/templates'),
  saveTemplate: (data) => api.post('/api/staff-leave/setup/templates', data),
  deleteTemplate: (id) => api.delete(`/api/staff-leave/setup/templates/${id}`),
  listCalendars: () => api.get('/api/staff-leave/setup/calendars'),
  saveCalendar: (data) => api.post('/api/staff-leave/setup/calendars', data),
  deleteCalendar: (id) => api.delete(`/api/staff-leave/setup/calendars/${id}`),
  listPeople: () => api.get('/api/staff-leave/setup/people'),
  savePerson: (userId, data) => api.put(`/api/staff-leave/setup/people/${userId}`, data),
};

export const rmAPI = {
  listStudents: (params) => api.get('/api/rm/students', { params }),
  getStudent: (id) => api.get(`/api/rm/students/${id}`),
  getCandidates: () => api.get('/api/rm/candidates'),
  assign: (id, data) => api.patch(`/api/rm/students/${id}/assign`, data),
  assignBulk: (data) => api.post('/api/rm/students/assign-bulk', data),
  listFeedback: (id) => api.get(`/api/rm/students/${id}/feedback`),
  addFeedback: (id, data) => api.post(`/api/rm/students/${id}/feedback`, data),
  listAssessments: (id) => api.get(`/api/rm/students/${id}/assessments`),
  getAssessment: (id, reportId) => api.get(`/api/rm/students/${id}/assessments/${reportId}`),
  listLeaveFollowups: (params) => api.get('/api/rm/leave-followups', { params }),
  completeLeaveFollowup: (id, data) => api.patch(`/api/rm/leave-followups/${id}`, data),
};

export const parentMeetingsAPI = {
  list: (params) => api.get('/api/rm/parent-meetings', { params }),
  searchStudents: (params) => api.get('/api/rm/parent-meetings/students', { params }),
  getStudent: (id) => api.get(`/api/rm/parent-meetings/students/${id}`),
  siblings: (id) => api.get(`/api/rm/parent-meetings/students/${id}/siblings`),
  calendar: (params) => api.get('/api/rm/parent-meetings/calendar', { params }),
  create: (data) => api.post('/api/rm/parent-meetings', data),
  update: (id, data) => api.patch(`/api/rm/parent-meetings/${id}`, data),
  remove: (id) => api.delete(`/api/rm/parent-meetings/${id}`),
};

export const classObservationsAPI = {
  list: (params) => api.get('/api/teaching/class-observations', { params }),
  getSession: (sessionId) => api.get(`/api/teaching/class-observations/sessions/${sessionId}`),
  add: (sessionId, data) => api.post(`/api/teaching/class-observations/sessions/${sessionId}`, data),
};

export const ticketsAPI = {
  create: (data) => {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return api.post('/api/tickets', data, {
        timeout: 120000,
        transformRequest: [
          (body, headers) => {
            if (typeof headers?.delete === 'function') headers.delete('Content-Type');
            else if (headers) delete headers['Content-Type'];
            return body;
          },
        ],
      });
    }
    return api.post('/api/tickets', data);
  },
  downloadAttachment: (ticketId, attachmentId) =>
    api.get(`/api/tickets/${ticketId}/attachments/${attachmentId}/file`, {
      responseType: 'blob',
      timeout: 120000,
    }),
  list: (params) => api.get('/api/tickets', { params }),
  getInbox: (params) => api.get('/api/tickets/inbox', { params }),
  get: (id) => api.get(`/api/tickets/${id}`),
  addComment: (id, data) => api.post(`/api/tickets/${id}/comments`, data),
  updateStatus: (id, data) => api.patch(`/api/tickets/${id}/status`, data),
  reassign: (id, data) => api.patch(`/api/tickets/${id}/assign`, data),
  getSummary: (params) => api.get('/api/tickets/meta/summary', { params }),
  previewAssignment: (params) => api.get('/api/tickets/meta/preview-assignment', { params }),
};

export const helpAPI = {
  getTopics: () => api.get('/api/help/meta/topics'),
  listArticles: (params) => api.get('/api/help/articles', { params }),
  getArticle: (id) => api.get(`/api/help/articles/${encodeURIComponent(id)}`),
  sendFeedback: (id, data) => api.post(`/api/help/articles/${encodeURIComponent(id)}/feedback`, data),
  suggestQuestion: (data) => api.post('/api/help/gaps', data),
  downloadAttachment: (attachmentId) =>
    api.get(`/api/help/attachments/${encodeURIComponent(attachmentId)}/download`, {
      responseType: 'blob',
    }),
  adminListArticles: (params) => api.get('/api/help/admin/articles', { params }),
  adminGetArticle: (id) => api.get(`/api/help/admin/articles/${encodeURIComponent(id)}`),
  adminCreateArticle: (data) => api.post('/api/help/admin/articles', data),
  adminUpdateArticle: (id, data) => api.put(`/api/help/admin/articles/${encodeURIComponent(id)}`, data),
  adminSetStatus: (id, data) => api.patch(`/api/help/admin/articles/${encodeURIComponent(id)}/status`, data),
  adminDeleteArticle: (id) => api.delete(`/api/help/admin/articles/${encodeURIComponent(id)}`),
  adminUploadAttachment: (id, formData) =>
    api.post(`/api/help/admin/articles/${encodeURIComponent(id)}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  adminSupersedeAttachment: (attachmentId) =>
    api.post(`/api/help/admin/attachments/${encodeURIComponent(attachmentId)}/supersede`),
  adminListGaps: (params) => api.get('/api/help/admin/gaps', { params }),
  adminUpdateGap: (id, data) => api.patch(`/api/help/admin/gaps/${encodeURIComponent(id)}`, data),
  adminSeedFromDisk: () => api.post('/api/help/admin/seed-from-disk'),
};

export const newsAPI = {
  getUpcomingBirthdays: (params) => api.get('/api/news/upcoming-birthdays', { params }),
  getUpdates: () => api.get('/api/news/updates'),
};

export const teacherModuleAPI = {
  getHome: (params) => api.get('/api/teacher/home', { params }),
  getUpcomingBatches: (params) => api.get('/api/teacher/upcoming-batches', { params }),
  getMyBatches: (params) => api.get('/api/teacher/my-batches', { params }),
  getMyPendingItems: (params) => api.get('/api/teacher/pending-items', { params }),
  getBatchWeeklyPlan: (batchId, params) => api.get(`/api/teacher/batches/${batchId}/weekly-plan`, { params }),
  upsertBatchWeeklyPlan: (batchId, payload) => api.put(`/api/teacher/batches/${batchId}/weekly-plan`, payload),
  getLeaveBatchesForDate: (params) => api.get('/api/teacher/leave/batches-for-date', { params }),
  getSessionDetail: (sessionId) => api.get(`/api/teacher/sessions/${sessionId}/detail`),
  listSessionPhotos: (sessionId) => api.get(`/api/teacher/sessions/${sessionId}/photos`),
  uploadSessionPhotos: (sessionId, formData) =>
    api.post(`/api/teacher/sessions/${sessionId}/photos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  replaceSessionPhoto: (sessionId, photoId, formData) =>
    api.put(`/api/teacher/sessions/${sessionId}/photos/${photoId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteSessionPhoto: (sessionId, photoId) =>
    api.delete(`/api/teacher/sessions/${sessionId}/photos/${photoId}`),
  getSessionPhotoFile: (sessionId, photoId) =>
    api.get(`/api/teacher/sessions/${sessionId}/photos/${photoId}/file`, {
      responseType: 'blob',
      timeout: 60000,
    }),
  getClassLogOptions: (params) => api.get('/api/teacher/class-log/options', { params }),
  startSession: (sessionId, data) => api.post(`/api/teacher/sessions/${sessionId}/start`, data || {}),
  cancelSession: (sessionId, data) => api.post(`/api/teacher/sessions/${sessionId}/cancel`, data),
  uncancelSession: (sessionId) => api.post(`/api/teacher/sessions/${sessionId}/uncancel`),
  upsertSessionLog: (sessionId, data) => api.put(`/api/teacher/sessions/${sessionId}/log`, data),
  getSessionApproval: (sessionId) => api.get(`/api/teacher/sessions/${sessionId}/approval`),
  requestSessionApproval: (sessionId, data) =>
    api.post(`/api/teacher/sessions/${sessionId}/approval/request`, data || {}),
  decideSessionApproval: (sessionId, data) => api.patch(`/api/teacher/sessions/${sessionId}/approval/decide`, data),
  listPendingApprovals: () => api.get('/api/teacher/approvals/pending'),
  uploadSessionHomework: (sessionId, formData) =>
    api.post(`/api/teacher/sessions/${sessionId}/homework/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  uploadSessionHomeworkImage: (sessionId, formData) =>
    api.post(`/api/teacher/sessions/${sessionId}/homework/images`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  deleteSessionHomeworkImage: (sessionId, imageId) =>
    api.delete(`/api/teacher/sessions/${sessionId}/homework/images/${imageId}`),
  generateSessionHomeworkPdf: (sessionId, payload) =>
    api.post(`/api/teacher/sessions/${sessionId}/homework/generate`, payload),
  listSessionHomeworkPdfs: (sessionId) => api.get(`/api/teacher/sessions/${sessionId}/homework-pdfs`),
  markSessionHomeworkSent: (sessionId, payload) =>
    api.post(`/api/teacher/sessions/${sessionId}/homework/mark-sent`, payload),
  getBatchPendingItems: (batchId) => api.get(`/api/teacher/batches/${batchId}/pending-items`),
  generateHomeworkPdf: (sessionId) => api.get(`/api/teacher/sessions/${sessionId}/homework-pdf`),
  getHomeworkTemplates: () => api.get('/api/teacher/homework/templates'),
  createHomeworkTemplate: (data) => api.post('/api/teacher/homework/templates', data),
  uploadHomeworkTemplate: (formData) =>
    api.post('/api/teacher/homework/templates/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  createLeaveRequest: (data) => api.post('/api/teacher/leave/requests', data),
  getMyLeaveRequests: () => api.get('/api/teacher/leave/requests/my'),
  getAllLeaveRequests: (params) => api.get('/api/teacher/leave/requests', { params }),
  getLeaveRequest: (requestId) => api.get(`/api/teacher/leave/requests/${requestId}`),
  getPendingLeaveRequests: () => api.get('/api/teacher/leave/requests/pending'),
  decideLeaveRequest: (requestId, data) =>
    api.patch(`/api/teacher/leave/requests/${requestId}/decision`, data),
  updateLeaveBatchCoverage: (requestId, data) =>
    api.patch(`/api/teacher/leave/requests/${requestId}/batch-coverage`, data),
  getLeaveApprovalBatches: (requestId) =>
    api.get(`/api/teacher/leave/requests/${requestId}/approval-batches`),
  getSessionAvailableSubstitutes: (sessionId) =>
    api.get(`/api/teacher/sessions/${sessionId}/available-substitutes`),
  getBatchAvailableSubstitutes: (batchId, date) =>
    api.get(`/api/teacher/batches/${batchId}/available-substitutes`, { params: { date } }),
  getSessionMergeCandidates: (sessionId, params) =>
    api.get(`/api/teacher/sessions/${sessionId}/merge-candidates`, { params }),
  assignSessionSubstitute: (sessionId, payload) =>
    api.post(`/api/teacher/sessions/${sessionId}/assign-substitute`, payload),
  mergeSession: (sessionId, payload) => api.post(`/api/teacher/sessions/${sessionId}/merge`, payload),
  confirmZoomParticipant: (sessionId, participantId, payload) =>
    api.post(
      `/api/teacher/sessions/${sessionId}/zoom/participants/${encodeURIComponent(participantId)}/confirm`,
      payload
    ),
  getMakeupClasses: (params) => api.get('/api/teacher/makeup-classes', { params }),
  getSubstitutionHistory: (params) => api.get('/api/teacher/substitution-history', { params }),
  getStudentAttendance: (params) => api.get('/api/teacher/student-attendance', { params }),
  getStudentAttendanceSessions: (studentId, params) =>
    api.get(`/api/teacher/student-attendance/${studentId}/sessions`, { params }),
  getTeachingHandover: (studentId, params) =>
    api.get(`/api/teacher/students/${studentId}/teaching-handover`, { params }),
  getTeachingHandoverAvailability: (studentId, params) =>
    api.get(`/api/teacher/students/${studentId}/teaching-handover/availability`, { params }),
  getMakeupClass: (sessionId) => api.get(`/api/teacher/makeup-classes/${sessionId}`),
  createMakeupClass: (data) => api.post('/api/teacher/makeup-classes', data),
  updateMakeupClass: (sessionId, data) => api.patch(`/api/teacher/makeup-classes/${sessionId}`, data),
  cancelMakeupClass: (sessionId) => api.delete(`/api/teacher/makeup-classes/${sessionId}`),
  getMonthlyReports: (params) => api.get('/api/teacher/monthly-reports', { params }),
  getMonthlyReport: (month) => api.get(`/api/teacher/monthly-reports/${month}`),
  getStudentTaughtNotes: (month, params) =>
    api.get('/api/teacher/monthly-reports/student-taught', { params: { month, ...params } }),
  getStudentTaughtNote: (month, studentId) =>
    api.get(`/api/teacher/monthly-reports/student-taught/${month}/students/${studentId}`),
  saveStudentTaughtNote: (month, data) =>
    api.put(`/api/teacher/monthly-reports/student-taught/${month}`, data),
  regenerateMonthlyReport: (month) => api.post(`/api/teacher/monthly-reports/${month}/regenerate`),
  updateMonthlyReport: (month, data) => api.put(`/api/teacher/monthly-reports/${month}`, data),
  submitMonthlyReport: (month) => api.post(`/api/teacher/monthly-reports/${month}/submit`),
  listMonthlyReportSubmissions: (params) =>
    api.get('/api/teacher/monthly-reports/submissions', { params }),
  reviewMonthlyReport: (reportId, data) =>
    api.patch(`/api/teacher/monthly-reports/submissions/${reportId}/review`, data),
  getStudentAssessmentCatalog: () => api.get('/api/teacher/student-monthly-reports/catalog'),
  listStudentMonthlyReports: (params) => api.get('/api/teacher/student-monthly-reports', { params }),
  getStudentMonthlyReport: (studentId, month) =>
    api.get(`/api/teacher/student-monthly-reports/${studentId}/${month}`),
  saveStudentMonthlyReport: (studentId, month, data) =>
    api.put(`/api/teacher/student-monthly-reports/${studentId}/${month}`, data),
  submitStudentMonthlyReport: (studentId, month) =>
    api.post(`/api/teacher/student-monthly-reports/${studentId}/${month}/submit`),
  getStudentReportReadiness: (params) => api.get('/api/teacher/student-monthly-reports/readiness', { params }),
  listStudentAssessmentSubmissions: (params) =>
    api.get('/api/teacher/student-monthly-reports/submissions/list', { params }),
  getStudentAssessmentSubmission: (reportId) =>
    api.get(`/api/teacher/student-monthly-reports/submissions/${reportId}`),
  reviewStudentAssessment: (reportId, data) =>
    api.patch(`/api/teacher/student-monthly-reports/submissions/${reportId}/review`, data),
  updateStudentAssessmentSubmission: (reportId, data) =>
    api.put(`/api/teacher/student-monthly-reports/submissions/${reportId}`, data),
  listParentMonthlyReports: (params) => api.get('/api/teacher/parent-monthly-reports', { params }),
  getParentMonthlyReport: (studentId, month) =>
    api.get(`/api/teacher/parent-monthly-reports/${studentId}/${month}`),
  sendParentMonthlyReport: (studentId, month) =>
    api.post(`/api/teacher/parent-monthly-reports/${studentId}/${month}/send`),
  getAssessmentCatalogAdmin: () => api.get('/api/teacher/assessment-catalog/modules'),
  createAssessmentCategory: (data) => api.post('/api/teacher/assessment-catalog/categories', data),
  updateAssessmentCategory: (id, data) => api.patch(`/api/teacher/assessment-catalog/categories/${id}`, data),
  createAssessmentModule: (data) => api.post('/api/teacher/assessment-catalog/modules', data),
  updateAssessmentModule: (id, data) => api.patch(`/api/teacher/assessment-catalog/modules/${id}`, data),
  createAssessmentParameter: (data) => api.post('/api/teacher/assessment-catalog/parameters', data),
  updateAssessmentParameter: (id, data) => api.patch(`/api/teacher/assessment-catalog/parameters/${id}`, data),
  createAssessmentOption: (data) => api.post('/api/teacher/assessment-catalog/options', data),
  updateAssessmentOption: (id, data) => api.patch(`/api/teacher/assessment-catalog/options/${id}`, data),
  getPublicParentReport: (token) => api.get(`/api/public/parent-reports/view/${token}`),
  getViewableTeachers: () => api.get('/api/teacher/hierarchy/viewable-teachers'),
  getDayCheckinToday: () => api.get('/api/teacher/day-checkin/today'),
  markDayCheckin: () => api.post('/api/teacher/day-checkin'),
  getDayCheckinMine: (month) => api.get('/api/teacher/day-checkin/mine', { params: { month } }),
  getDayCheckinTeam: (params) => api.get('/api/teacher/day-checkin/team', { params }),
};

export const teacherAdminAPI = {
  getSupervisorRoster: () => api.get('/api/teacher-admin/supervisor-roster'),
  updateSupervisorTeam: (supervisorTeacherId, data) =>
    api.put(`/api/teacher-admin/supervisors/${supervisorTeacherId}/teachers`, data),
};

export const callsAPI = {
  status: () => api.get('/api/calls/status'),
  log: (contactId) => api.get('/api/calls/log', { params: { contactId } }),
  permission: (bpContactId) => api.get('/api/calls/permission', { params: { bpContactId } }),
  requestPermission: (data) => api.post('/api/calls/permission-request', data),
  setDoNotCall: (data) => api.put('/api/calls/do-not-call', data),
  accept: (callId, tabId) => api.post(`/api/calls/${callId}/accept`, { tabId, device: 'web' }),
  decline: (callId) => api.post(`/api/calls/${callId}/decline`, {}),
  hangup: (callId) => api.post(`/api/calls/${callId}/hangup`, {}),
  start: (data) => api.post('/api/calls/start', data),
};

export const whatsappAPI = {
  listChats: (params) => api.get('/api/whatsapp/chats', { params }),
  chatCounts: () => api.get('/api/whatsapp/chats/counts'),
  getChat: (contactId) => api.get(`/api/whatsapp/chats/${contactId}`),
  listMessages: (contactId, params) => api.get(`/api/whatsapp/chats/${contactId}/messages`, { params }),
  reply: (contactId, data) => api.post(`/api/whatsapp/chats/${contactId}/reply`, data),
  listCampaigns: () => api.get('/api/whatsapp/campaigns'),
  sendTemplate: (contactId, data) => api.post(`/api/whatsapp/chats/${contactId}/template`, data, { timeout: 30000 }),
  uploadMedia: (contactId, file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/api/whatsapp/chats/${contactId}/media`, form, {
      timeout: 180000,
      transformRequest: [(data, headers) => {
        if (typeof headers?.delete === 'function') headers.delete('Content-Type');
        else if (headers) delete headers['Content-Type'];
        return data;
      }],
    });
  },
  react: (contactId, data) => api.post(`/api/whatsapp/chats/${contactId}/reactions`, data),
  markRead: (contactId, data) => api.post(`/api/whatsapp/chats/${contactId}/read`, data),
  unreadCount: () => api.get('/api/whatsapp/unread-count'),
  listUnassigned: (params) => api.get('/api/whatsapp/unassigned', { params }),
  linkUnassigned: (contactId, data) => api.post(`/api/whatsapp/unassigned/${contactId}/link`, data),
  dismissUnassigned: (contactId) => api.post(`/api/whatsapp/unassigned/${contactId}/dismiss`),
  listAssignees: (params) => api.get('/api/whatsapp/assignees', { params }),
};

export const notificationsAPI = {
  list: (params) => api.get('/api/notifications', { params }),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  getImportantUpdates: () => api.get('/api/notifications/important-updates'),
  markRead: (id) => api.patch(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/mark-all-read'),
  markBatchRead: (batchId) => api.post(`/api/notifications/mark-batch-read/${batchId}`),
  markStudentRead: (studentId) => api.post(`/api/notifications/mark-student-read/${studentId}`),
};

// Finance APIs
export const financeAPI = {
  // Pricing matrix
  listPricing: (params) => api.get('/api/finance/pricing-matrix', { params }),
  createPricing: (data) => api.post('/api/finance/pricing-matrix', data),
  updatePricing: (id, data) => api.patch(`/api/finance/pricing-matrix/${id}`, data),
  getQuote: (params) => api.get('/api/finance/pricing/quote', { params }),

  // Subscriptions
  createSubscription: (data) => api.post('/api/finance/subscriptions', data),
  createSubscriptionFull: (data) => api.post('/api/finance/subscriptions/full', data),
  previewSubscription: (data) => api.post('/api/finance/subscriptions/preview', data),
  notifyParentSubscription: (id) => api.post(`/api/finance/subscriptions/${id}/notify-parent`),
  updateSubscription: (id, data) => api.patch(`/api/finance/subscriptions/${id}`, data),
  serviceAdjustment: (id, data) => api.post(`/api/finance/subscriptions/${id}/service-adjustment`, data),
  deleteSubscription: (id, data) => api.delete(`/api/finance/subscriptions/${id}`, { data }),
  getSubscriptionChangeHistory: (id) => api.get(`/api/finance/subscriptions/${id}/change-history`),
  listSubscriptions: (params) => api.get('/api/finance/subscriptions', { params }),
  getSubscription: (id) => api.get(`/api/finance/subscriptions/${id}`),
  getSubscriptionsByStudent: (studentId) =>
    api.get(`/api/finance/subscriptions/by-student/${studentId}`),
  createNextInvoice: (subscriptionId) =>
    api.post(`/api/finance/subscriptions/${subscriptionId}/next-invoice`),
  getStudentsWithoutSubscription: (params) =>
    api.get('/api/finance/students-without-subscription', { params }),
  listAdmissionStudents: (params) => api.get('/api/finance/admission/students', { params }),
  getInvoicesForSubscription: (subscriptionId) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/invoices`),
  getPaymentsForSubscription: (subscriptionId) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/payments`),
  recordPayment: (subscriptionId, data) =>
    api.post(`/api/finance/subscriptions/${subscriptionId}/payments`, data),
  getOverduePayments: (params) => api.get('/api/finance/payments/overdue', { params }),
  getDueTodayPayments: (params) => api.get('/api/finance/payments/due-today', { params }),
  getRecentCollections: (params) => api.get('/api/finance/payments/recent-collections', { params }),
  getDiscountsForSubscription: (subscriptionId) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/discounts`),
  createSubscriptionDiscount: (subscriptionId, data) =>
    api.post(`/api/finance/subscriptions/${subscriptionId}/discounts`, data),
  updateSubscriptionDiscount: (subscriptionId, discountId, data) =>
    api.patch(
      `/api/finance/subscriptions/${subscriptionId}/discounts/${discountId}`,
      data
    ),
  deleteSubscriptionDiscount: (subscriptionId, discountId, data) =>
    api.delete(
      `/api/finance/subscriptions/${subscriptionId}/discounts/${discountId}`,
      { data }
    ),
  getAddOnsForSubscription: (subscriptionId) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/add-ons`),
  createSubscriptionAddOn: (subscriptionId, data) =>
    api.post(`/api/finance/subscriptions/${subscriptionId}/add-ons`, data),
  deleteSubscriptionAddOn: (subscriptionId, addOnId, data) =>
    api.delete(`/api/finance/subscriptions/${subscriptionId}/add-ons/${addOnId}`, { data }),
  triggerPaymentReminder: (paymentId, data) =>
    api.post(`/api/finance/payments/${paymentId}/reminders`, data),
  getBillingSummary: (subscriptionId, params) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/billing-summary`, { params }),
  getDiscountCatalog: () => api.get('/api/finance/catalog/discount-types'),
  updateDiscountCatalogType: (type, data) =>
    api.patch(`/api/finance/catalog/discount-types/${type}`, data),
  getAddOnCatalog: () => api.get('/api/finance/catalog/add-on-types'),
  listAddOnPricing: (params) => api.get('/api/finance/add-on-pricing', { params }),
  createAddOnPricing: (data) => api.post('/api/finance/add-on-pricing', data),
  updateAddOnPricing: (id, data) => api.patch(`/api/finance/add-on-pricing/${id}`, data),
  deleteAddOnPricing: (id) => api.delete(`/api/finance/add-on-pricing/${id}`),

  // Razorpay
  getRazorpayStatus: () => api.get('/api/finance/razorpay/status'),
  getRazorpayDefaultCustomer: (subscriptionId) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/razorpay/default-customer`),
  listRazorpayInvoices: (subscriptionId) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/razorpay/invoices`),
  composeRazorpayInvoice: (subscriptionId, invoiceId, params) =>
    api.get(`/api/finance/subscriptions/${subscriptionId}/invoices/${invoiceId}/razorpay/compose`, {
      params,
    }),
  checkRazorpayInvoiceNumber: (params) =>
    api.get('/api/finance/razorpay/invoice-number-available', { params }),
  saveRazorpayDraft: (subscriptionId, invoiceId, data) =>
    api.post(`/api/finance/subscriptions/${subscriptionId}/invoices/${invoiceId}/razorpay/draft`, data),
  updateRazorpayDraft: (localId, data) =>
    api.patch(`/api/finance/razorpay/invoices/${localId}/draft`, data),
  createRazorpayInvoice: (subscriptionId, invoiceId, data) =>
    api.post(`/api/finance/subscriptions/${subscriptionId}/invoices/${invoiceId}/razorpay`, data),
  notifyRazorpayInvoice: (localId, data) =>
    api.post(`/api/finance/razorpay/invoices/${localId}/notify`, data),
  cancelRazorpayInvoice: (localId) =>
    api.post(`/api/finance/razorpay/invoices/${localId}/cancel`),
  refundRazorpayPayment: (paymentId, data) =>
    api.post(`/api/finance/payments/${paymentId}/razorpay-refund`, data),
};

// Admin – user management
export const adminUsersAPI = {
  getRoles: () => api.get('/api/admin/users/roles'),
  list: (params) => api.get('/api/admin/users', { params }),
  create: (data) => api.post('/api/admin/users', data),
  update: (id, data) => api.patch(`/api/admin/users/${id}`, data),
  updateRole: (id, role) => api.patch(`/api/admin/users/${id}/role`, { role }),
  updateGlobalTeachingOps: (id, globalTeachingOps) =>
    api.patch(`/api/admin/users/${id}/global-teaching-ops`, { globalTeachingOps }),
  resetPassword: (id, newPassword) =>
    api.post(`/api/admin/users/${id}/reset-password`, { newPassword }),
  previewEmailChange: (id, email) =>
    api.post(`/api/admin/users/${id}/email/preview`, { email }),
  changeEmail: (id, email) => api.post(`/api/admin/users/${id}/email`, { email }),
};

export const apiUtils = {
  downloadFile: (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
  formatErrorMessage: (error) => {
    if (error.response?.data?.message) {
      return error.response.data.message;
    }
    if (error.response?.data?.errors) {
      return Array.isArray(error.response.data.errors) 
        ? error.response.data.errors.join(', ')
        : error.response.data.errors;
    }
    return error.message || 'An error occurred';
  },
  handleError: (error, customMessage = null) => {
    const message = customMessage || apiUtils.formatErrorMessage(error);
    console.error('API Error:', error);
    return { success: false, error: message };
  },
};

export default api; 